import { FilterItem } from '@mm/components';
import { UIFilterStarterKindType } from '@mm/state/model/ChatStarterModel';
import { selectChatStartersCountByUiFilterKind } from '@mm/state/selectors/chatStarters';
import {
  combinedFeatureFlagsSelector,
  companyFeatureFlagsSelector,
} from '@mm/state/selectors/featureFlagsSelectors';
import { selectIsSmsSupported } from '@mm/state/selectors/twillio';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as InstagramIcon } from 'assets/instagram.svg';
import { ReactComponent as FBIcon } from 'assets/messenger-fb-new.svg';
import { ReactComponent as SMSIcon } from 'assets/smartphone.svg';
import { ReactComponent as WebchatIcon } from 'assets/webchat-new.svg';
import classNames from 'classnames';
import { keys } from 'lodash';
import React, { ReactFragment } from 'react';
import { useSelector } from 'react-redux';
import { trackWebAppGAEvent } from '../../../../../../common/helpers/plugins/website-google-analytics';
import { useGreenCueMarkers } from '../../../../../../common/hooks/use-green-cue-markers';
import { useFreeTrialUpgradeModal } from '../../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { starterTypeByKind } from '../types';
import './StarterFilterByKind.pcss';

type Props = {
  className?: string;
  activeKind: UIFilterStarterKindType;
  onChange: (kind?: UIFilterStarterKindType) => void;
};

type StarterFilterType =
  | 'fbWebchat'
  | 'facebook'
  | 'instagram'
  | 'sms'
  | 'website';

type StarterFilterByTypeProps = {
  kindsToShow: UIFilterStarterKindType[];
  activeKind: UIFilterStarterKindType;
  type: StarterFilterType;
  onChange: (kind?: UIFilterStarterKindType) => void;
  isActive?: boolean;
};

const kindsByType: Record<StarterFilterType, UIFilterStarterKindType[]> = {
  fbWebchat: ['customer_chat_widget'],
  facebook: [
    'facebook_page_welcomer',
    'comment_guard',
    'share_links',
    'landing_pages',
    'facebook_optin',
  ],
  instagram: [
    'instagram_welcomer',
    'instagram_comment_guard',
    'instagram_story_mention',
    'instagram_story_reaction',
    'instagram_live_comment_guard',
  ],
  sms: ['sms_keywords'],
  website: ['optin_popups'],
};

const typeTitles: Record<StarterFilterType, string | ReactFragment> = {
  fbWebchat: 'FB & Webchat',
  facebook: 'Facebook',
  instagram: (
    <div className="starter-filter-by-type__ig">
      <div>Instagram</div>
    </div>
  ),
  sms: 'SMS',
  website: 'Website',
};

export const getIconByType = (type: StarterFilterType) => {
  switch (type) {
    case 'fbWebchat':
      return (
        <div className="fb-webchat-icon-container">
          <WebchatIcon className="icon-by-type" />
          <FBIcon className="icon-by-type" />
        </div>
      );
    case 'facebook':
      return <FBIcon className="icon-by-type" />;
    case 'instagram':
      return <InstagramIcon className="icon-by-type" />;
    case 'sms':
      return <SMSIcon className="icon-by-type" />;
    case 'website':
    default:
      return <WebchatIcon className="icon-by-type" />;
  }
};

const StarterFilterBlockByType = ({
  type,
  kindsToShow,
  onChange,
  activeKind,
}: StarterFilterByTypeProps) => {
  const icon = getIconByType(type);

  return (
    <div>
      <div className="starter-filter-by-type__heading">
        {icon}
        {typeTitles[type]}
        <div className="starter-filter-by-type__heading-line" />
      </div>
      {kindsToShow.map(kind => (
        <StarterFilterByKindItem
          key={kind}
          kind={kind}
          onClick={() => {
            trackWebAppGAEvent('click', {
              category: 'starter_filter_by_type',
              label: `filter_button_${kind}`,
            });
            onChange(kind);
          }}
          isActive={kind === activeKind}
        />
      ))}
    </div>
  );
};

const StarterFilterByKindItem = ({
  kind,
  onClick,
  isActive,
}: {
  kind: UIFilterStarterKindType;
  onClick: (kind?: UIFilterStarterKindType) => void;
  isActive?: boolean;
}) => {
  const totalStartersCount = useSelector((state: StoreState) =>
    selectChatStartersCountByUiFilterKind(state, kind),
  );

  const { isWebChatActivated, isCommentGuardActivated, isSmsActivated } =
    useGreenCueMarkers();
  const { isFreeTrialPromotionWithoutSubscription } =
    useFreeTrialUpgradeModal();

  const showActivationCues = useSelector(
    combinedFeatureFlagsSelector,
  ).show_cues;

  const starter = starterTypeByKind[kind];
  if (!starter) return null;

  const { icon: Icon, name } = starter;
  const showCustomerChatGreenMarker =
    kind === 'customer_chat_widget' && !isWebChatActivated;
  const showSmsKeywordGreenMarker = kind === 'sms_keywords' && !isSmsActivated;
  const showCommentGuardGreenMarker =
    kind === 'comment_guard' && !isCommentGuardActivated;
  const showNewLabel = kind === 'instagram_live_comment_guard';
  const showGreenMarker =
    showActivationCues &&
    isFreeTrialPromotionWithoutSubscription &&
    (showCustomerChatGreenMarker ||
      showCommentGuardGreenMarker ||
      showSmsKeywordGreenMarker);

  const count =
    ['facebook_page_welcomer', 'instagram_welcomer'].indexOf(kind) !== -1
      ? undefined
      : totalStartersCount;
  return (
    <FilterItem<UIFilterStarterKindType | undefined>
      title={name}
      item={kind}
      icon={Icon}
      onClick={onClick}
      isActive={isActive}
      count={count}
      showGreenMarker={showGreenMarker}
      showNewLabel={showNewLabel}
    />
  );
};

export const StarterFilterByKind = ({
  className,
  activeKind,
  onChange = () => {},
}: Props) => {
  const isSmsSupported = useSelector(selectIsSmsSupported);

  const types = keys(kindsByType) as StarterFilterType[];
  const typesToShow = types.filter(type => {
    return !((type === 'sms' || type === 'website') && !isSmsSupported);
  });

  return (
    <div className={classNames(className, 'starter-filter-by-kind')}>
      {typesToShow.map(type => (
        <StarterFilterBlockByType
          key={type}
          type={type}
          kindsToShow={kindsByType[type]}
          onChange={onChange}
          activeKind={activeKind}
        />
      ))}
    </div>
  );
};
